import { ComponentProps, useCallback, useEffect, useState } from 'react'

import { Button, Icon } from '@clientbase/clientbase-library'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useRouter } from 'next/router'

import { AccountAlert, fetchAccountAlerts } from '../../api/accountAlertsApi'
import {
  ACCOUNT_ALERTS_CHANGED,
  dismissAlertForToday,
  isAlertDismissedToday
} from '../../utils/dismissedAlerts'

// Famílias semânticas do DESIGN.md (wash de fundo, base na borda, mid no texto).
const COLORS: Record<
  AccountAlert['severity'],
  { bg: string; border: string; fg: string }
> = {
  error: { bg: '#FFFAFB', border: '#CC2942', fg: '#990017' },
  warning: { bg: '#FFFCF7', border: '#F1A841', fg: '#66471C' },
  info: { bg: '#F7FBFF', border: '#4C98FF', fg: '#294266' }
}

const ICONS: Record<
  AccountAlert['severity'],
  ComponentProps<typeof Icon>['icon']
> = {
  error: 'error',
  warning: 'error',
  info: 'info'
}

// SC-11813: mesmo formato do banner do changelog, mas preso ao topo de todas as
// páginas e sem botão de fechar: fica até a empresa resolver (a API deixa de
// devolver o aviso). Recarrega a cada troca de página e quando a aba volta ao foco.
// SC-11817: avisos `dismissible` (pesquisa de NPS) ganham um X e, fechados, só
// voltam no dia seguinte.
export default function AccountAlertBanner() {
  const { pathname, query, push } = useRouter()
  const [alerts, setAlerts] = useState<AccountAlert[]>([])
  // Fechar um aviso muda só o localStorage: força o re-render do filtro.
  const [, setDismissVersion] = useState(0)

  const load = useCallback(async () => {
    try {
      setAlerts(await fetchAccountAlerts())
    } catch {
      setAlerts([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [pathname, load])

  useEffect(() => {
    const onAlertsChanged = () => {
      setDismissVersion((version) => version + 1)
      load()
    }
    window.addEventListener('focus', load)
    window.addEventListener(ACCOUNT_ALERTS_CHANGED, onAlertsChanged)
    return () => {
      window.removeEventListener('focus', load)
      window.removeEventListener(ACCOUNT_ALERTS_CHANGED, onAlertsChanged)
    }
  }, [load])

  // "?open_nps=true" abre a ação na própria página (a pesquisa é um modal);
  // caminho completo navega.
  const openAction = (url: string) => {
    if (!url.startsWith('?')) {
      push(url)
      return
    }
    const extra = Object.fromEntries(new URLSearchParams(url))
    push({ pathname, query: { ...query, ...extra } }, undefined, {
      shallow: true
    })
  }

  const visibleAlerts = alerts.filter(
    (alert) => !alert.dismissible || !isAlertDismissedToday(alert.type)
  )

  if (!visibleAlerts.length) return null

  return (
    // As páginas já têm 20px de padding no topo; a Visão Geral começa com o fundo
    // verde colado no topo e precisa do espaço embaixo do banner.
    <Stack
      gap={1}
      padding={pathname === '/visao-geral' ? '20px' : '20px 20px 0 20px'}
    >
      {visibleAlerts.map((alert) => {
        const colors = COLORS[alert.severity] || COLORS.error
        return (
          <Box
            key={alert.type}
            role={alert.severity === 'info' ? 'status' : 'alert'}
            sx={{
              backgroundColor: colors.bg,
              border: `1px solid ${colors.border}`,
              color: colors.fg,
              px: 2.5,
              py: 1.25,
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              alignItems: 'center',
              gap: 2,
              borderRadius: 2
            }}
          >
            <Icon
              icon={ICONS[alert.severity] || ICONS.error}
              sx={{ color: colors.fg }}
            />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography
                component="p"
                sx={{ display: 'block', fontSize: 14, fontWeight: 600 }}
              >
                {alert.title}
              </Typography>
              <Typography
                component="p"
                sx={{ display: 'block', fontSize: 13, lineHeight: 1.4 }}
              >
                {alert.message}
              </Typography>
            </Box>
            {alert.action_url && alert.action_label && (
              <Button
                variantButton="primary"
                buttonSize="sm"
                onClick={() => alert.action_url && openAction(alert.action_url)}
              >
                {alert.action_label}
              </Button>
            )}
            {alert.dismissible && (
              <Tooltip title="Fechar até amanhã" arrow>
                <IconButton
                  size="small"
                  aria-label="Fechar aviso até amanhã"
                  onClick={() => dismissAlertForToday(alert.type)}
                  sx={{ color: colors.fg }}
                >
                  <Icon icon="close" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}
